import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Split } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RupiahInput from "@/components/RupiahInput";
import { formatIDR } from "@/lib/constants";
import api from "@/lib/api";

/**
 * 149-cw-r185: Dialog Pengalihan Kas — mengubah 1 item pengajuan menjadi
 * banyak item realisasi (nama/qty berbeda), TANPA mengubah nominal transfer.
 * Item asli disimpan sebagai snapshot audit; setiap event tercatat.
 */
export default function PettyCashReallocateDialog({ open, pc, onCancel, onSubmit }) {
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Carry-over: sisa saldo minggu2 sebelumnya ikut menambah saldo tersedia (2026-09-27).
  const [carry, setCarry] = useState(null);

  const transferAmount = pc?.transfer_amount ?? pc?.total ?? 0;
  const originalSnapshot = useMemo(() => (pc?.items_original?.length ? pc.items_original : pc?.items || []), [pc]);

  useEffect(() => {
    if (open && pc) {
      // Seed dgn items eksisting supaya pengaju bisa edit incremental.
      setRows((pc.items || []).map((it) => ({
        _key: crypto.randomUUID(),
        name: it.name || "",
        qty: it.qty ?? 1,
        unit: it.unit || "",
        unit_price: it.unit_price ?? 0,
        note: it.note || "",
      })));
      setReason("");
      setCarry(null);
      api.get(`/petty-cash/${pc.id}/carryover`).then((r) => setCarry(r.data)).catch(() => setCarry(null));
    }
  }, [open, pc]);

  const total = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.qty) || 0) * (parseFloat(r.unit_price) || 0), 0), [rows]);
  const carryover = carry?.carryover ?? 0;
  const available = transferAmount + carryover;
  const sisa = available - total;
  const overBudget = total > available + 1;

  const addRow = () => setRows((s) => [...s, { _key: crypto.randomUUID(), name: "", qty: 1, unit: "", unit_price: 0, note: "" }]);
  const removeRow = (i) => setRows((s) => s.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    if (rows.length === 0) return;
    if (overBudget) return;
    setBusy(true);
    try {
      await onSubmit({
        new_items: rows.map((r) => ({
          name: (r.name || "").trim(),
          qty: parseFloat(r.qty) || 1,
          unit: (r.unit || "").trim() || null,
          unit_price: parseFloat(r.unit_price) || 0,
          note: (r.note || "").trim() || null,
        })),
        reason: reason.trim() || null,
      });
    } finally { setBusy(false); }
  };

  if (!pc) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rounded-none w-[92vw] max-w-[92vw] max-h-[90vh] overflow-y-auto" data-testid="dlg-pc-reallocate">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <Split size={18} className="text-amber-600" /> Pengalihan Kas — Ubah Item Realisasi
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="border-l-2 border-amber-500 bg-amber-50 p-3 text-[11px] text-amber-900">
            Fitur ini mengubah <b>item realisasi</b> tanpa mengubah nominal yang sudah ditransfer.
            Item asli tersimpan sebagai snapshot audit. Setelah dialihkan, isi realisasi (nominal aktual)
            lagi untuk setiap item baru — approver akan memverifikasi ulang.
          </div>

          {/* Snapshot item asli */}
          <div>
            <div className="overline text-[10px] font-semibold text-slate-500 mb-1">Item Asli Pengajuan</div>
            <div className="border border-slate-200 bg-slate-50/50 p-2 text-[11px]">
              {originalSnapshot.map((it) => (
                <div key={it.id} className="flex justify-between py-0.5">
                  <span>{it.name} <span className="text-slate-400">× {it.qty}{it.unit ? ` ${it.unit}` : ""}</span></span>
                  <span className="tabular-nums font-semibold text-slate-700">{formatIDR(it.subtotal || 0)}</span>
                </div>
              ))}
              <div className="border-t border-slate-300 mt-1 pt-1 flex justify-between font-semibold">
                <span>Total Ditransfer</span>
                <span className="tabular-nums text-emerald-700">{formatIDR(transferAmount)}</span>
              </div>
              {carry && carry.carryover !== 0 && (
                <div className="flex justify-between text-sky-800" data-testid="realloc-carryover">
                  <span>+ Sisa saldo minggu sebelumnya</span>
                  <span className="tabular-nums font-semibold">{formatIDR(carry.carryover)}</span>
                </div>
              )}
              {carry && (
                <div className="flex justify-between font-bold border-t border-slate-200 pt-0.5" data-testid="realloc-available">
                  <span>Saldo Tersedia Minggu Ini</span>
                  <span className="tabular-nums text-[#0B1D3A]">{formatIDR(available)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Editor item baru */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="overline text-[10px] font-semibold text-slate-500">Item Realisasi Baru</div>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="btn-realloc-add-row" className="rounded-none h-7 text-[10px]">
                <Plus size={12} className="mr-1" /> Tambah Item
              </Button>
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1 text-left w-1/3">Nama Item / Jasa</th>
                    <th className="px-2 py-1 text-right w-16">Qty</th>
                    <th className="px-2 py-1 text-left w-20">Satuan</th>
                    <th className="px-2 py-1 text-right w-32">Harga Satuan</th>
                    <th className="px-2 py-1 text-right w-28">Subtotal</th>
                    <th className="px-2 py-1 text-left">Catatan</th>
                    <th className="px-2 py-1 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const sub = (parseFloat(r.qty) || 0) * (parseFloat(r.unit_price) || 0);
                    return (
                      <tr key={r._key} className="border-b border-slate-100">
                        <td className="px-1 py-1">
                          <Input data-testid={`realloc-name-${i}`} value={r.name} onChange={(e) => updateRow(i, { name: e.target.value })} className="rounded-none h-8 text-xs" placeholder="mis. BBM survey" />
                        </td>
                        <td className="px-1 py-1">
                          <Input data-testid={`realloc-qty-${i}`} type="number" min="0" step="1" value={r.qty} onChange={(e) => updateRow(i, { qty: e.target.value })} className="rounded-none h-8 text-xs text-right tabular-nums w-14" />
                        </td>
                        <td className="px-1 py-1">
                          <Input data-testid={`realloc-unit-${i}`} value={r.unit} onChange={(e) => updateRow(i, { unit: e.target.value })} className="rounded-none h-8 text-xs w-20" placeholder="pcs" />
                        </td>
                        <td className="px-1 py-1">
                          <RupiahInput testid={`realloc-price-${i}`} value={r.unit_price} onChange={(v) => updateRow(i, { unit_price: v })} className="h-8" />
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums font-semibold text-slate-700" data-testid={`realloc-subtotal-${i}`}>{formatIDR(sub)}</td>
                        <td className="px-1 py-1">
                          <Input data-testid={`realloc-note-${i}`} value={r.note} onChange={(e) => updateRow(i, { note: e.target.value })} className="rounded-none h-8 text-xs" placeholder="opsional…" />
                        </td>
                        <td className="px-1 py-1 text-center">
                          {rows.length > 1 && (
                            <button type="button" onClick={() => removeRow(i)} data-testid={`realloc-remove-${i}`} className="text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td colSpan={4} className="px-2 py-1 text-right uppercase text-[10px] tracking-wider">Total Realisasi</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${overBudget ? "text-rose-700" : "text-slate-800"}`} data-testid="realloc-total">{formatIDR(total)}</td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr className="border-t border-slate-200 bg-slate-50 text-[10px]">
                    <td colSpan={4} className="px-2 py-1 text-right uppercase tracking-wider text-slate-500">
                      {overBudget ? "Melebihi Saldo Tersedia" : "Sisa Kas (Return)"}
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums font-semibold ${overBudget ? "text-rose-700" : "text-emerald-700"}`} data-testid="realloc-sisa">
                      {formatIDR(overBudget ? total - available : sisa)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {overBudget && (
              <div className="text-[10px] text-rose-700 mt-1 font-semibold">
                Total item realisasi tidak boleh melebihi saldo tersedia ({formatIDR(available)}
                = transfer {formatIDR(transferAmount)} + sisa minggu sebelumnya {formatIDR(carryover)}).
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Alasan Pengalihan (opsional)</label>
            <Textarea data-testid="realloc-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none text-sm" rows={2} placeholder="mis. dana dialihkan ke BBM & konsumsi karena survey diperluas" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-none">Batal</Button>
          <Button
            data-testid="btn-realloc-submit"
            onClick={submit}
            disabled={busy || overBudget || rows.length === 0 || rows.some((r) => !(r.name || "").trim())}
            className="rounded-none bg-amber-600 hover:bg-amber-700 text-white"
          >{busy ? "Menyimpan…" : "Simpan Pengalihan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
